import type { HTMLAttributes } from 'react';
import {
  RATE_FRACTION_DIGITS,
  TOTAL_FRACTION_DIGITS,
  formatMoney,
  type TotalAmount,
  type UnitRate,
} from '@/lib/money';
import styles from './MoneyText.module.css';

export type MoneyTone = 'text' | 'muted' | 'primary' | 'buy' | 'sell' | 'success' | 'danger';
export type MoneySize = 'caption' | 'body' | 'h3' | 'h2' | 'h1' | 'display';

type MoneyTextBaseProps = HTMLAttributes<HTMLSpanElement> & {
  /** 'TRY', 'USD'… Verilmezse yalniz sayi basilir. */
  currency?: string;
  /**
   * Ondalik hane. Verilmezse ikisi de 2 — `offer_price` numeric(11,2) oldugu icin
   * birim kurda daha fazlasi olmayan bir hassasiyet ima eder.
   */
  fractionDigits?: number;
  tone?: MoneyTone;
  size?: MoneySize;
};

/**
 * BIRIM KUR ile TOPLAM ayni bilesenden gecer AMA ayni propla GECMEZ.
 *
 * `offer_price` bir BIRIM KURDUR; islemin toplami offerPrice * taskAmount'tir.
 * Ikisi de sadece `number` olsaydi biri digerinin yerine gecebilirdi — gecmiste
 * gecti de ve ekranlarda 34,21 TL'lik islemler gorundu. Bu yuzden:
 *
 *   <MoneyText rate={unitRate(offer.offer_price)} currency="TRY" />
 *   <MoneyText total={totalOf(unitRate(offer.offer_price), task.amount)} currency="TRY" />
 *
 * Markali tipler (UnitRate / TotalAmount) sayesinde ham bir `number`, ya da birim
 * kurun toplam yerine verilmesi DERLEME HATASI olur. Ikisini ayni anda vermek de
 * mumkun degil (`rate?: never` / `total?: never`).
 */
export type MoneyTextProps = MoneyTextBaseProps &
  ({ rate: UnitRate; total?: never } | { total: TotalAmount; rate?: never });

export function MoneyText({
  currency,
  fractionDigits,
  tone = 'text',
  size = 'body',
  className,
  rate,
  total,
  ...rest
}: MoneyTextProps) {
  const isRate = rate !== undefined;
  // Birlesim tipi en az birinin tanimli oldugunu garanti eder; TS bunu
  // destructuring sonrasinda takip edemedigi icin tek daraltma burada.
  const value: number = isRate ? rate : total;
  const digits = fractionDigits ?? (isRate ? RATE_FRACTION_DIGITS : TOTAL_FRACTION_DIGITS);

  const formatted = formatMoney(value, digits);
  // Para birimi sayidan ayrilmasin diye satir kirilmasi CSS'te kapali (.money).
  const text = currency ? `${formatted} ${currency}` : formatted;

  return (
    <span
      // Testler ve hata ayiklama icin: bu deger BIRIM KUR mu, TOPLAM mi?
      data-money-kind={isRate ? 'rate' : 'total'}
      className={['money', styles.money, styles[tone], styles[size], className]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {text}
    </span>
  );
}
